// @ts-strict-ignore
import { DateInputBlock } from '@coast/schemas';
import { isNil } from 'lodash';
import React, { useCallback, useContext, useState } from 'react';
import { Keyboard, TouchableOpacity } from 'react-native';
import { useImmediateLayoutAnimation } from 'use-layout-animation';
import { View } from '@shared/components/View';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { Range } from '@shared/components/dateAndTime/comboDatePicker/props';
import { DateString } from '@shared/scenes/cards/agenda/utils';
import { MBCardInputBlockBase } from '../input/base';
import { renderClearButton } from '../input/utils';
import { NoCodeInputBlockDate } from './NoCodeInputBlockDate';
import { MBCardInputBlockDateContent } from './content';
import { MBCardInputBlockDatePicker } from './picker';
import { PickerMode } from './types';

interface Props {
  block: DateInputBlock;
  workflowTemplateId?: string;
}

export function CardInputBlockDate({ workflowTemplateId, block }: Props) {
  if (isNil(workflowTemplateId)) {
    return <MBCardInputBlockDate block={block} />;
  } else {
    return <NoCodeInputBlockDate block={block} workflowTemplateId={workflowTemplateId} />;
  }
}

export function MBCardInputBlockDate({ block }: Props) {
  const { values, setFieldValue } = useContext(CardFormContext);
  const value = values[block.name];
  const anchorName = `--input-block-date-${block.name}`;

  const [pickerMode, setPickerMode] = useState<PickerMode>();
  useImmediateLayoutAnimation([pickerMode]);

  const handleOpenPicker = useCallback((mode: PickerMode) => {
    setPickerMode(mode);
    Keyboard?.dismiss();
  }, []);
  const handleClosePicker = useCallback(() => setPickerMode(undefined), []);
  const handlePress = useCallback(() => {
    if (block.fieldType === 'time') {
      handleOpenPicker(PickerMode.Time);
    } else {
      handleOpenPicker(PickerMode.Date);
    }
  }, [handleOpenPicker, block.fieldType]);

  const onChange = useCallback(
    (updatedValue: Range<DateString>) => setFieldValue(block.name, updatedValue.start),
    [block.name, setFieldValue],
  );

  const renderRight = useCallback(
    (inputBlock: DateInputBlock) => {
      if (!value) {
        return null;
      }
      if (!!inputBlock.label) {
        return <View paddingTop={2}>{renderClearButton(inputBlock, handleClosePicker)}</View>;
      }
      return renderClearButton(inputBlock);
    },
    [value, handleClosePicker],
  );

  return (
    <>
      <TouchableOpacity onPress={!!pickerMode ? handleClosePicker : handlePress}>
        <View style={{ anchorName }}>
          <MBCardInputBlockBase block={block} renderRight={renderRight}>
            <MBCardInputBlockDateContent
              block={block}
              pickerMode={pickerMode}
              onOpenPicker={handleOpenPicker}
              onClosePicker={handleClosePicker}
            />
          </MBCardInputBlockBase>
        </View>
      </TouchableOpacity>
      {!!pickerMode && (
        <MBCardInputBlockDatePicker
          mode={pickerMode}
          block={block}
          anchorName={anchorName}
          onChange={onChange}
          onClose={handleClosePicker}
        />
      )}
    </>
  );
}
